/**
 * 공통 폼 클래스 – submit via fetch, validate, loader, disabled
 * CSRF: 폼에 _token hidden이 있으면 FormData에 포함됨. 필요 시 X-CSRF-TOKEN 메타에서 읽어 헤더 추가 가능.
 *
 * Accept: application/json + X-Requested-With: XMLHttpRequest 전송으로 서버에서
 * $request->expectsJson() 이 true 가 됨. 따라서 컨트롤러에서 expectsJson() 기준으로
 * JSON 응답(Response::json) 또는 리다이렉트/에러 페이지로 분기 가능.
 * 예외 발생 시 ExceptionHandler 도 wantsJson() 으로 JSON 에러 응답 반환.
 */

import { Logger } from "../utils/Logger";
import { Modal } from "../utils/Modal";
import { Toast, type ToastHandle } from "../utils/Toast";
import { t } from "../utils/I18n";

export interface FormToastOptions {
  /** 같은 id를 쓰면 저장중 toast 하나가 완료/실패 상태로 갱신됨 */
  id?: string;
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

export interface FormOptions {
  /** POST URL (없으면 form.action 또는 data-submit-url 사용) */
  submitUrl?: string;
  /** 클라이언트 검증: 오류 메시지 반환 또는 null (async 지원) */
  validate?: (form: HTMLFormElement) => string | null | Promise<string | null>;
  /** 제출 버튼 선택자 (기본 'button[type="submit"]') */
  submitButtonSelector?: string;
  /** 로더 숨김/표시할 요소 선택자 (버튼 내부) */
  loaderSelector?: string;
  /** 제출 중 버튼 라벨로 잠시 교체할 텍스트(예: "제출 중..."). 종료 시 원본 복원. */
  submittingText?: string;
  /** 성공 시 (응답 JSON, 파싱된 data) */
  onSuccess?: (data: Record<string, unknown>) => void | Promise<void>;
  /** 실패 시 (메시지, 응답 data) */
  onFail?: (message: string, data?: Record<string, unknown>) => void | Promise<void>;
  /** 제출 상태 변화 시 UI 동기화 */
  onSubmittingChange?: (submitting: boolean, form: HTMLFormElement) => void;
  /** 요청 실패(네트워크 등) 시 기본 메시지 */
  requestFailedMessage?: string;
  /** JSON 파싱 실패 시 기본 메시지 */
  invalidResponseMessage?: string;
  /** 저장 상태 toast. 생략 시 admin ajax/CMS form에서 자동 활성화 */
  toast?: boolean | FormToastOptions;
}

const DEFAULT_SUBMIT_SELECTOR = 'button[type="submit"]';

export interface NormalizedFormResponse {
  success: boolean;
  message: string;
  payload: Record<string, unknown>;
  raw: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function normalizeFormResponse(data: Record<string, unknown>): NormalizedFormResponse {
  const result = data.result ?? data.success;
  const success = result === "success" || result === true;
  const message = String(data.msg ?? data.message ?? "");
  const nested = isRecord(data.data) ? data.data : {};
  return {
    success,
    message,
    payload: { ...nested, ...data },
    raw: data,
  };
}

function dispatchFormEvent(
  name: "admin:form:success" | "admin:form:error" | "admin:form:bound",
  form: HTMLFormElement,
  detail: Record<string, unknown> = {},
): void {
  window.dispatchEvent(new CustomEvent(name, { detail: { form, ...detail } }));
  form.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
}

export class Form {
  private readonly form: HTMLFormElement;
  private readonly opts: Required<Pick<FormOptions, "submitButtonSelector" | "loaderSelector">> &
    FormOptions;
  private originalButtonHtml: string | null = null;
  private submitToast: ToastHandle | null = null;
  private isSubmitting = false;

  constructor(form: HTMLFormElement, options: FormOptions = {}) {
    this.form = form;
    this.opts = {
      submitButtonSelector: options.submitButtonSelector ?? DEFAULT_SUBMIT_SELECTOR,
      loaderSelector: options.loaderSelector ?? "",
      ...options,
    };
  }

  init(): void {
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    dispatchFormEvent("admin:form:bound", this.form);
  }

  private getSubmitUrl(): string | null {
    return (
      this.opts.submitUrl ??
      (this.form.action || this.form.dataset.submitUrl || null)
    );
  }

  private getSubmitButton(): HTMLButtonElement | null {
    const sel = this.opts.submitButtonSelector;
    const internal = this.form.querySelector<HTMLButtonElement>(sel);
    if (internal) return internal;
    // form="id" 로 폼 외부에 선언된 submit 버튼도 탐색 (nested form 방지 패턴)
    const formId = this.form.id;
    if (formId) {
      return document.querySelector<HTMLButtonElement>(`${sel}[form="${formId}"]`);
    }
    return null;
  }

  private getLoader(): HTMLElement | null {
    const btn = this.getSubmitButton();
    if (!btn || !this.opts.loaderSelector) return null;
    return btn.querySelector<HTMLElement>(this.opts.loaderSelector);
  }

  private shouldShowSubmitToast(): boolean {
    if (this.silentSubmit) return false;
    const toast = this.opts.toast;
    if (toast === false) return false;
    if (toast === true || typeof toast === "object") return true;

    const isAdminAjaxForm = this.form.matches('form[data-admin-ajax="1"]');
    const isCmsForm = this.form.matches(
      'form[data-cms-modal-form], form[data-cms-inline-editor]',
    );
    const isCmsInspectorForm = this.form.closest("[data-cms-inspector-body]") != null;

    return document.body.classList.contains("admin")
      && (isAdminAjaxForm || isCmsForm || isCmsInspectorForm);
  }

  private getToastOptions(): FormToastOptions {
    return typeof this.opts.toast === "object" ? this.opts.toast : {};
  }

  private startSubmitToast(): void {
    if (!this.shouldShowSubmitToast()) return;

    const toast = this.getToastOptions();
    this.submitToast = Toast.loading(
      toast.loadingMessage ?? "저장중...",
      toast.id ?? "form-submit",
    );
  }

  private finishSubmitToast(success: boolean, message?: string): void {
    if (!this.submitToast) return;

    const toast = this.getToastOptions();
    if (success) {
      this.submitToast.success(toast.successMessage ?? message ?? "저장완료");
    } else {
      this.submitToast.error(message || toast.errorMessage || "저장 실패");
    }
    this.submitToast = null;
  }

  private silentSubmit = false;

  setSubmitting(submitting: boolean): void {
    const btn = this.getSubmitButton();
    // silentSubmit(자동저장 등 백그라운드 제출): 버튼 로더/disable/텍스트 스왑을 생략한다.
    // 수동 저장 클릭 시에만 푸터 스피너가 보이도록 하고, 자동저장은 별도 상태 표시만 사용.
    if (btn && !this.silentSubmit) {
      btn.disabled = submitting;
      btn.dataset.submitting = submitting ? "1" : "0";
      // 버튼 라벨을 "제출 중..." 텍스트로 잠시 교체. loaderSelector 가 있으면 텍스트만 교체해도 로더가 함께 보임.
      // 로더 display 토글은 이 라벨 스왑/복원 '이후'에 해야 한다. 복원(btn.innerHTML =
      // originalButtonHtml)이 캡처 시점의 인라인 style 을 통째로 되돌리므로, 로더를 먼저
      // 숨겨도 복원이 display:inline-block 을 되살려 스피너가 계속 도는 버그가 있었다.
      if (this.opts.submittingText) {
        if (submitting) {
          if (this.originalButtonHtml === null) {
            this.originalButtonHtml = btn.innerHTML;
          }
          // 표준 CTA는 라벨 span을 사용하고, 기존 버튼은 직접 텍스트 노드를 사용할 수 있다.
          const label = btn.querySelector<HTMLElement>("[data-submit-label]");
          if (label) {
            label.textContent = this.opts.submittingText;
          } else {
            for (const node of Array.from(btn.childNodes)) {
              if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
                node.textContent = this.opts.submittingText + " ";
                break;
              }
            }
          }
        } else if (this.originalButtonHtml !== null) {
          btn.innerHTML = this.originalButtonHtml;
          this.originalButtonHtml = null;
        }
      }
      // 라벨 복원 후 최신 로더 요소를 다시 조회해 표시 상태를 확정한다.
      const loader = this.getLoader();
      if (loader) {
        loader.hidden = !submitting;
      }
    }
    this.form.dataset.submitting = submitting ? "1" : "0";
    this.form.setAttribute("aria-busy", submitting ? "true" : "false");
    this.opts.onSubmittingChange?.(submitting, this.form);
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    // 클릭 이벤트가 빠르게 연속 발생하거나 Enter 제출이 겹쳐도 하나의 요청만 허용한다.
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    // form[data-silent-submit] 로 표시된 백그라운드 제출은 버튼 로더/토스트를 숨긴다.
    this.silentSubmit = this.form.dataset.silentSubmit === "1";

    if (this.opts.validate) {
      const errorMsg = await Promise.resolve(this.opts.validate(this.form));
      if (errorMsg != null) {
        if (errorMsg === "") {
          this.isSubmitting = false;
          return;
        }
        if (this.opts.onFail) await this.opts.onFail(errorMsg);
        else await Modal.alert(errorMsg);
        this.isSubmitting = false;
        return;
      }
    }

    const url = this.getSubmitUrl();
    if (!url) {
      Logger.warn("Form: no submit URL");
      this.isSubmitting = false;
      return;
    }

    const body = new FormData(this.form);

    this.setSubmitting(true);
    this.startSubmitToast();

    try {
      // HTTP 메서드는 항상 POST로 유지. _method 필드를 body에 남겨서
      // 서버 Request::method()의 스푸핑 로직이 PATCH/PUT/DELETE로 처리하게 한다.
      // PHP는 multipart/form-data를 POST에서만 자동 파싱하므로 실제 PATCH/PUT/DELETE
      // 전송 시 $_POST가 비어 CSRF 토큰을 읽지 못한다.
      const res = await fetch(url, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const raw = await res.text();
      let data: Record<string, unknown> = {};
      try {
        data = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        const msg =
          this.opts.invalidResponseMessage ?? t("form.invalid_response");
        this.finishSubmitToast(false, msg);
        dispatchFormEvent("admin:form:error", this.form, { message: msg });
        if (this.opts.onFail) await this.opts.onFail(msg);
        else await Modal.alert(msg);
        return;
      }

      const normalized = normalizeFormResponse(data);
      const msg = normalized.message;

      if (normalized.success) {
        this.finishSubmitToast(true, msg);
        dispatchFormEvent("admin:form:success", this.form, {
          data,
          normalized,
        });
        if (this.opts.onSuccess) await this.opts.onSuccess(data);
        else await Modal.alert(msg || t("form.success"));
      } else {
        this.finishSubmitToast(false, msg || t("form.error"));
        dispatchFormEvent("admin:form:error", this.form, {
          message: msg || t("form.error"),
          data,
          normalized,
        });
        if (this.opts.onFail) await this.opts.onFail(msg || t("form.error"), data);
        else await Modal.alert(msg || t("form.error"));
      }
    } catch {
      const msg = this.opts.requestFailedMessage ?? t("form.request_failed");
      this.finishSubmitToast(false, msg);
      dispatchFormEvent("admin:form:error", this.form, { message: msg });
      if (this.opts.onFail) await this.opts.onFail(msg);
      else await Modal.alert(msg);
      Logger.error("Form submit failed", url);
    } finally {
      this.setSubmitting(false);
      this.silentSubmit = false;
      this.isSubmitting = false;
    }
  }
}
